import React, { useEffect, useMemo, useState } from "react";
import { fetchMilestonesTimeline } from "../services/milestonesClient";
import { fetchSalesClosingsSummary } from "../services/salesClosingsSummaryClient";
import FormSection from "../components/FormSection";
import "../styles/utilities.css";
import "../styles/TimelineBoard.css";

const PROJECTS = [
  { id: "Vida", label: "Vida", colorClass: "marker-color-vida", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=187404&Listings=true&Amenities=true", timelineEnabled: true },
  { id: "Fusion", label: "Fusion", colorClass: "marker-color-fusion", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=187403&Listings=true&Amenities=true", timelineEnabled: true },
  { id: "SoMi B", label: "SoMi B", colorClass: "marker-color-somib", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=160201&Listings=true&Amenities=true", timelineEnabled: true },
  { id: "SoMi A", label: "SoMi A", colorClass: "marker-color-somia", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=160201&Listings=true&Amenities=true", timelineEnabled: true },
  { id: "SoMi Towns", label: "SoMi Towns", colorClass: "marker-color-somitowns", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=160201&Listings=true&Amenities=true", timelineEnabled: true },
  { id: "Aria", label: "Aria", colorClass: "marker-color-aria", mapUrl: "https://salesarchitect.exsquared.com/ISP-3?bdxCommunityID=187403&Listings=true&Amenities=true", timelineEnabled: true },
];

const TIMELINE_PROJECTS = PROJECTS.filter(project => project.timelineEnabled);
const PROJECT_COLORS = PROJECTS.reduce((acc, p) => ({ ...acc, [p.id]: p.colorClass }), {});
const STATUS_ORDER = [
  { numeric: 2, label: "Backlog" },
  { numeric: 4, label: "For Sale" },
  { numeric: 5, label: "Pending" },
];
const SALES_CLOSINGS_ROWS = [
  { key: "projectedClosings", label: "Projected Closings" },
  { key: "closings", label: "Closings" },
  { key: "projectedSales", label: "Projected Sales" },
  { key: "sales", label: "Sales" },
];
const SALES_CLOSINGS_PROJECTS = [
  { id: "Aria", label: "Aria", plan: "6/24", colorClass: "marker-color-aria" },
  { id: "Fusion", label: "Fusion", plan: "0/15", colorClass: "marker-color-fusion" },
  { id: "SoMi A", label: "SoMi A", plan: "30/45", colorClass: "marker-color-somia" },
  { id: "SoMi B", label: "SoMi B", plan: "2/2", colorClass: "marker-color-somib" },
  { id: "SoMi Towns", label: "SoMi Towns", plan: "26/47", colorClass: "marker-color-somitowns" },
  { id: "Vida", label: "Vida", plan: "6/6", colorClass: "marker-color-vida" },
];
const SALES_CLOSINGS_PLAN = {
  "2026-01": 0,
  "2026-02": 0,
  "2026-03": 7,
  "2026-04": 7,
  "2026-05": 9,
  "2026-06": 10,
  "2026-07": 11,
  "2026-08": 8,
  "2026-09": 7,
  "2026-10": 9,
  "2026-11": 9,
  "2026-12": 5,
};
const SALES_CLOSINGS_PLAN_CUME = (() => {
  const entries = Object.keys(SALES_CLOSINGS_PLAN).sort();
  let running = 0;
  return entries.reduce((acc, month) => {
    running += SALES_CLOSINGS_PLAN[month];
    acc[month] = running;
    return acc;
  }, {});
})();
const SALES_PLACEHOLDER = "—";

function startOfMonth(month) {
  if (!month) return null;
  return `${month}-01`;
}

function endOfMonth(month) {
  if (!month) return null;
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return null;
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}

function buildMonths(startMonth, endMonth) {
  if (!startMonth || !endMonth || startMonth > endMonth) return [];
  const [startY, startM] = startMonth.split("-").map(Number);
  const [endY, endM] = endMonth.split("-").map(Number);
  const months = [];
  let y = startY;
  let m = startM;
  while (y < endY || (y === endY && m <= endM)) {
    months.push(`${y.toString().padStart(4, "0")}-${m.toString().padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return months;
}

function formatMonthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

function deriveDefaultRange() {
  const now = new Date();
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const startMonth = `${prevDate.getFullYear()}-${(prevDate.getMonth() + 1).toString().padStart(2, "0")}`;

  const nextDate = new Date(prevDate.getFullYear(), prevDate.getMonth() + 11, 1);
  const endMonth = `${nextDate.getFullYear()}-${(nextDate.getMonth() + 1).toString().padStart(2, "0")}`;

  return { startMonth, endMonth };
}

function monthFromISO(date) {
  if (!date || typeof date !== "string") return "";
  const match = date.match(/^(\d{4})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }
  return "";
}

function determineTargetMonth(unit, numericStatus, visibleStartMonth) {
  const sales = unit.sales_status || {};
  if (numericStatus === 2) {
    const normalizeVisibleMonth = (month) => {
      if (!month) return "";
      if (visibleStartMonth && month < visibleStartMonth) {
        return visibleStartMonth;
      }
      return month;
    };

    let target = null;
    if (sales.coe_date && monthFromISO(sales.coe_date)) {
      target = monthFromISO(sales.coe_date);
    } else if (sales.projected_closing_date && monthFromISO(sales.projected_closing_date)) {
      target = monthFromISO(sales.projected_closing_date);
    } else if (sales.fully_executed_date && monthFromISO(sales.fully_executed_date)) {
      target = monthFromISO(sales.fully_executed_date);
    } else if (sales.week_ratified_date && monthFromISO(sales.week_ratified_date)) {
      target = monthFromISO(sales.week_ratified_date);
    }

    if (target) {
      if (visibleStartMonth && target < visibleStartMonth) {
        // Keep overdue backlog visible on the board by pinning it to the first
        // visible month when there is no newer projected close to use instead.
        if (sales.extended_adjusted_coe && monthFromISO(sales.extended_adjusted_coe)) {
          return normalizeVisibleMonth(monthFromISO(sales.extended_adjusted_coe));
        } else if (unit.ops_projected_coe && monthFromISO(unit.ops_projected_coe)) {
          return normalizeVisibleMonth(monthFromISO(unit.ops_projected_coe));
        } else {
          return visibleStartMonth;
        }
      }
      return target;
    } else if (unit.ops_projected_coe && monthFromISO(unit.ops_projected_coe)) {
      return normalizeVisibleMonth(monthFromISO(unit.ops_projected_coe));
    }
  } else if (numericStatus === 4 || numericStatus === 5) {
    if (unit.ops_projected_coe && monthFromISO(unit.ops_projected_coe)) {
      return monthFromISO(unit.ops_projected_coe);
    } else if (sales.projected_closing_date && monthFromISO(sales.projected_closing_date)) {
      return monthFromISO(sales.projected_closing_date);
    }
  }
  return "TBD";
}

export function TimelineRollupView() {
  const desktopStorageKey = "timelineRollupDesktopLayout";
  const { startMonth: defaultStart, endMonth: defaultEnd } = useMemo(deriveDefaultRange, []);
  const [startMonth, setStartMonth] = useState(defaultStart);
  const [endMonth, setEndMonth] = useState(defaultEnd);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salesSummary, setSalesSummary] = useState({
    closings_by_month: {},
    sales_by_month: {},
    closed_ytd_by_project: {},
    inventory_by_project: {},
  });
  const [salesSummaryLoading, setSalesSummaryLoading] = useState(false);
  const [salesSummaryError, setSalesSummaryError] = useState("");
  const [desktopLayout, setDesktopLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(desktopStorageKey);
    if (stored === null) return false;
    return stored === "true";
  });

  const months = useMemo(() => buildMonths(startMonth, endMonth), [startMonth, endMonth]);
  const currentMonthKey = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    return `${year}-${month}`;
  }, []);
  const salesClosingsSummary = useMemo(() => {
    const closingsByMonth = salesSummary.closings_by_month || {};
    const salesByMonth = salesSummary.sales_by_month || {};

    const closedByProject = SALES_CLOSINGS_PROJECTS.reduce((acc, project) => {
      acc[project.id] = salesSummary.closed_ytd_by_project?.[project.id] ?? 0;
      return acc;
    }, {});
    const inventoryByProject = SALES_CLOSINGS_PROJECTS.reduce((acc, project) => {
      acc[project.id] = salesSummary.inventory_by_project?.[project.id] ?? 0;
      return acc;
    }, {});

    const yearSet = new Set();
    Object.keys(closingsByMonth).forEach(month => yearSet.add(month.slice(0, 4)));
    Object.keys(salesByMonth).forEach(month => yearSet.add(month.slice(0, 4)));
    months.forEach(month => {
      if (month) yearSet.add(month.slice(0, 4));
    });

    const years = Array.from(yearSet).filter(Boolean).sort();
    const buildCume = (countsByMonth) => {
      const cume = {};
      years.forEach(year => {
        let running = 0;
        for (let i = 1; i <= 12; i += 1) {
          const key = `${year}-${String(i).padStart(2, "0")}`;
          running += countsByMonth[key] || 0;
          cume[key] = running;
        }
      });
      return cume;
    };

    return {
      closingsByMonth,
      salesByMonth,
      closingsCumeByMonth: buildCume(closingsByMonth),
      salesCumeByMonth: buildCume(salesByMonth),
      closedByProject,
      inventoryByProject,
    };
  }, [salesSummary, months]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(desktopStorageKey, desktopLayout ? "true" : "false");
  }, [desktopLayout]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      if (!startMonth || !endMonth || startMonth > endMonth) return;
      setLoading(true);
      setError("");
      try {
        const start = startOfMonth(startMonth);
        const end = endOfMonth(endMonth);

        // Fetch all timeline projects in parallel
        const responses = await Promise.all(
          TIMELINE_PROJECTS.map(p => fetchMilestonesTimeline({ project_id: p.id, start, end }))
        );

        if (cancelled) return;

        let allUnits = [];
        responses.forEach(res => {
          if (res?.units) {
            allUnits = [...allUnits, ...res.units];
          }
        });

        setUnits(allUnits);
      } catch (err) {
        if (!cancelled) setError(String(err?.message || err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [startMonth, endMonth]);

  useEffect(() => {
    let cancelled = false;
    async function fetchSalesSummary() {
      if (!startMonth || !endMonth || startMonth > endMonth) return;
      setSalesSummaryLoading(true);
      setSalesSummaryError("");
      try {
        const [startYear] = startMonth.split("-");
        // Pull the full visible start year so cumulative monthly totals stay YTD
        // even when the board view begins mid-year.
        const start = startYear ? `${startYear}-01-01` : startOfMonth(startMonth);
        const end = endOfMonth(endMonth);
        const data = await fetchSalesClosingsSummary({ start, end });
        if (!cancelled) {
          setSalesSummary(data || {});
        }
      } catch (err) {
        if (!cancelled) setSalesSummaryError(String(err?.message || err));
      } finally {
        if (!cancelled) setSalesSummaryLoading(false);
      }
    }
    fetchSalesSummary();
    return () => { cancelled = true; };
  }, [startMonth, endMonth]);

  function handleStartChange(val) {
    if (!val) return;
    if (endMonth && val > endMonth) setEndMonth(val);
    setStartMonth(val);
  }

  function handleEndChange(val) {
    if (!val) return;
    if (startMonth && val < startMonth) setStartMonth(val);
    setEndMonth(val);
  }

  // Group units by numeric status (row) and target month (column)
  const groupedData = useMemo(() => {
    const grid = {};
    STATUS_ORDER.forEach(st => {
      grid[st.numeric] = {};
      months.forEach(m => grid[st.numeric][m] = []);
      grid[st.numeric]["TBD"] = [];
    });

    units.forEach(unit => {
      const salesStatusNum = unit.sales_status?.statusnumeric;
      const statusNum = (salesStatusNum && salesStatusNum !== 99) ? salesStatusNum : unit.status;
      if (![2, 4, 5].includes(statusNum)) return; // Only process relevant statuses

      const m = determineTargetMonth(unit, statusNum, startMonth);
      const col = months.includes(m) ? m : "TBD";

      grid[statusNum][col].push(unit);
    });

    // Optionally sort units within cells (e.g., by project then unit number)
    Object.keys(grid).forEach(row => {
      Object.keys(grid[row]).forEach(col => {
        grid[row][col].sort((a, b) => {
          if (a.project_id !== b.project_id) return a.project_id.localeCompare(b.project_id);
          const numA = parseInt(a.unit_number) || 0;
          const numB = parseInt(b.unit_number) || 0;
          return numA - numB;
        });
      });
    });

    return grid;
  }, [units, months]);

  return (
    <div className={`page full-width ${desktopLayout ? "timeline-desktop" : ""}`} style={{ padding: '0 1rem' }}>
      <div className="page-header" style={{ marginBottom: '0.25rem', paddingBottom: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: 'none' }}>
        <h2 style={{ fontSize: '1.25rem', margin: 0 }}>Magnet Board</h2>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: '#6b7280' }}>
            <input
              type="checkbox"
              checked={desktopLayout}
              onChange={e => setDesktopLayout(e.target.checked)}
            />
            Desktop layout
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
            <span>Start</span>
            <input type="month" value={startMonth} onChange={e => handleStartChange(e.target.value)} style={{ padding: '0.1rem 0.2rem', fontSize: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '4px' }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
            <span>End</span>
            <input type="month" value={endMonth} onChange={e => handleEndChange(e.target.value)} style={{ padding: '0.1rem 0.2rem', fontSize: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '4px' }} />
          </label>
        </div>
      </div>

      <div className="timeline-board">
        {error && <div style={{ color: "#dc2626" }}>Error loading board: {error}</div>}
        {salesSummaryError && <div style={{ color: "#dc2626" }}>Error loading sales summary: {salesSummaryError}</div>}

        <div className="timeline-board-table-wrap">
          <div className="timeline-board-scale">
            <table className="timeline-board-table">
              <thead>
                <tr>
                  <th className="timeline-board-row-label">Status</th>
                  <th className="timeline-board-tbd-label"></th>
                  {months.map(month => (
                    <th key={month} className={month === currentMonthKey ? "current-month" : ""}>
                      {formatMonthLabel(month)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="sales-closings-row">
                  <td className="timeline-board-row-label sales-closings-label">
                    <div className="sales-closings-subtitle">Closings</div>
                    <div className="sales-closings-subtitle">Sales</div>
                  </td>
                  <td className="timeline-board-tbd-cell sales-closings-oob">
                    <table className="sales-closings-oob-table">
                      <tbody>
                        {SALES_CLOSINGS_ROWS.map((row, idx) => (
                          <tr key={row.key}>
                            {idx === 0 && (
                              <td className="sales-closings-oob-projects" rowSpan={SALES_CLOSINGS_ROWS.length}>
                                <div className="sales-closings-project-list">
                                  <div className="sales-closings-project sales-closings-project-spacer" aria-hidden="true" />
                                  {SALES_CLOSINGS_PROJECTS.map(project => (
                                    <div key={project.id} className={`sales-closings-project ${project.colorClass}`}>
                                      <span>{project.label}</span>
                                      <span>{project.plan}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            )}
                            {idx === 0 && (
                              <td className="sales-closings-oob-subtable" rowSpan={SALES_CLOSINGS_ROWS.length}>
                                  <div className="sales-closings-subtable">
                                  <div className="sales-closings-subtable-row sales-closings-subtable-header">
                                    <span>Closed</span>
                                    <span>Inv</span>
                                  </div>
                                  {SALES_CLOSINGS_PROJECTS.map(project => (
                                    <div key={`sub-${project.id}`} className="sales-closings-subtable-row">
                                      {(() => {
                                        const closedCount = salesSummaryLoading
                                          ? null
                                          : (salesClosingsSummary.closedByProject[project.id] ?? 0);
                                        const inventoryCount = salesSummaryLoading
                                          ? null
                                          : (salesClosingsSummary.inventoryByProject[project.id] ?? 0);
                                        return (
                                          <>
                                            <span className={closedCount === null ? "is-placeholder" : ""}>
                                              {closedCount === null ? SALES_PLACEHOLDER : closedCount}
                                            </span>
                                            <span className={inventoryCount === null ? "is-placeholder" : ""}>
                                              {inventoryCount === null ? SALES_PLACEHOLDER : inventoryCount}
                                            </span>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            )}
                            <td className="sales-closings-oob-label">
                              <span className="sales-closings-oob-label-text">
                                {row.label.replace("Projected", "Proj.")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                  {months.map(month => {
                    const projectedValue = SALES_CLOSINGS_PLAN[month];
                    const projectedCume = SALES_CLOSINGS_PLAN_CUME[month];
                    const closingsValue = salesSummaryLoading
                      ? null
                      : (salesClosingsSummary.closingsByMonth[month] ?? 0);
                    const closingsCume = salesSummaryLoading
                      ? null
                      : (salesClosingsSummary.closingsCumeByMonth[month] ?? 0);
                    const salesValue = salesSummaryLoading
                      ? null
                      : (salesClosingsSummary.salesByMonth[month] ?? 0);
                    const salesCume = salesSummaryLoading
                      ? null
                      : (salesClosingsSummary.salesCumeByMonth[month] ?? 0);
                    const salesClosingsValues = [
                      { key: "projectedClosings", value: projectedValue, cume: projectedCume },
                      { key: "closings", value: closingsValue, cume: closingsCume },
                      { key: "projectedSales", value: null, cume: null },
                      { key: "sales", value: salesValue, cume: salesCume },
                    ];

                    return (
                      <td key={`sales-closings-${month}`} className={month === currentMonthKey ? "current-month" : ""}>
                        <div className="sales-closings-grid">
                          {salesClosingsValues.map(row => (
                            <React.Fragment key={`${month}-${row.key}`}>
                              <div className={`sales-closings-cell ${row.value == null ? "is-placeholder" : ""}`}>
                                {row.value == null ? SALES_PLACEHOLDER : row.value}
                              </div>
                              <div className={`sales-closings-cell ${row.cume == null ? "is-placeholder" : ""}`}>
                                {row.cume == null ? SALES_PLACEHOLDER : row.cume}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {loading ? (
                  <tr>
                    <td colSpan={months.length + 2} style={{ textAlign: "center", padding: "3rem" }}>
                      Loading Magnet Board...
                    </td>
                  </tr>
                ) : (
                  STATUS_ORDER.map(st => (
                    <tr key={st.numeric}>
                      <td className="timeline-board-row-label">
                        {st.label}
                      </td>
                      <td className="timeline-board-tbd-cell">
                        <div className="timeline-cell-markers">
                          {(groupedData[st.numeric]["TBD"] || []).map((u, i) => (
                            <UnitMarker key={`${u.project_id}-${u.unit_number}-${i}-tbd`} unit={u} />
                          ))}
                        </div>
                      </td>
                      {months.map(month => {
                        const cellUnits = groupedData[st.numeric][month] || [];
                        return (
                          <td key={`${st.numeric}-${month}`} className={month === currentMonthKey ? "current-month" : ""}>
                            <div className="timeline-cell-markers">
                              {cellUnits.map((u, i) => (
                                <UnitMarker key={`${u.project_id}-${u.unit_number}-${i}`} unit={u} />
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function UnitMarker({ unit }) {
  const colorClass = PROJECT_COLORS[unit.project_id] || "marker-color-default";
  const salesStatusNum = unit.sales_status?.statusnumeric;
  const statusNum = (salesStatusNum && salesStatusNum !== 99) ? salesStatusNum : unit.status;
  const hideBuyer = statusNum === 4 || statusNum === 5;

  // Hard limit buyer name to 20 chars
  let buyerName = unit.sales_status?.buyer_combined || "";
  if (buyerName.length > 20) {
    buyerName = buyerName.substring(0, 20);
  }

  let bldgId = unit.building_id || "N/A";
  if (bldgId.startsWith("Building ")) {
    bldgId = bldgId.replace("Building ", "Bldg. ");
  }

  return (
    <div className={`unit-marker ${colorClass} ${hideBuyer ? 'compact' : ''}`} title={`${unit.project_id} - Unit ${unit.unit_number}`}>
      <div className="unit-marker-top">
        <span className="unit-marker-unit">{unit.unit_number}</span>
        <span className="unit-marker-building">{unit.project_id}</span>
      </div>
      {!hideBuyer && (
        <div className="unit-marker-middle">
          <span className="unit-marker-buyer" title={unit.sales_status?.buyer_combined || ""}>{buyerName}</span>
        </div>
      )}
      {hideBuyer && unit.ops_projected_coe && (
        <div className="unit-marker-middle ops-date-overlay" style={{ fontSize: '0.45rem', color: '#ffffff', fontWeight: 600, padding: '2px 0' }}>
          <span title="Ops Chalkboard Projection">Ops: {unit.ops_projected_coe.slice(5).replace('-', '/')}</span>
        </div>
      )}
      <div className="unit-marker-bottom" title={`${bldgId} | ${unit.uniqueness || ""} | ${unit.plan_type || ""}`}>
        <span className="marker-bld">{bldgId}</span>
        <span className="marker-uniq">{unit.uniqueness || ""}</span>
        <span className="marker-plan">{unit.plan_type || ""}</span>
      </div>
    </div>
  );
}

export default function TimelineRollup() {
  return <TimelineRollupView />;
}
